<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div
      :class="[
        theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ]"
    >
      <div class="flex items-center justify-between">
        <div>
          <h1
            :class="[theme.isDark.value ? 'text-gray-100' : 'text-gray-900', 'text-2xl font-bold']"
          >
            MongoDB Index Usage
          </h1>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-1 text-sm']">
            Monitor index performance across clusters and collections
          </p>
        </div>
        <router-link
          to="/database"
          :class="[
            theme.isDark.value
              ? 'text-gray-400 hover:text-gray-200'
              : 'text-gray-600 hover:text-gray-900',
            'text-sm font-medium'
          ]"
        >
          ← Back to Database
        </router-link>
      </div>
    </div>

    <div class="p-6 space-y-6">
      <!-- Selection Controls -->
      <div
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg overflow-hidden'
        ]"
      >
        <h2
          :class="[
            theme.isDark.value ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200',
            'text-lg font-semibold px-6 py-4 border-b'
          ]"
        >
          Select Collection
        </h2>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <!-- Environment Select -->
            <div>
              <label
                for="mongo-env-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Environment
              </label>
              <select
                id="mongo-env-select"
                v-model="mongoEnv"
                :disabled="clustersLoading"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
                @change="onEnvChange"
              >
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
            </div>

            <!-- Cluster Select -->
            <div>
              <label
                for="mongo-cluster-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Cluster
              </label>
              <select
                id="mongo-cluster-select"
                v-model="selectedClusterName"
                :disabled="clustersLoading || clusterList.length === 0"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
              >
                <option value="">{{ clustersLoading ? 'Loading...' : 'Select a cluster' }}</option>
                <option
                  v-for="c in clusterList"
                  :key="c.id"
                  :value="c.name"
                >
                  {{ c.name }}
                </option>
              </select>
            </div>

            <!-- Database Select -->
            <div>
              <label
                for="mongo-db-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Database
              </label>
              <select
                id="mongo-db-select"
                v-model="selectedDatabase"
                :disabled="databasesLoading || databases.length === 0"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
              >
                <option value="">
                  {{
                    databasesLoading
                      ? 'Loading...'
                      : selectedClusterName
                        ? 'Select a database'
                        : 'Select cluster first'
                  }}
                </option>
                <option
                  v-for="db in databases"
                  :key="db"
                  :value="db"
                >
                  {{ db }}
                </option>
              </select>
            </div>

            <!-- Collection Select -->
            <div>
              <label
                for="mongo-collection-select"
                :class="[
                  theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                  'block text-sm font-medium mb-2'
                ]"
              >
                Collection
              </label>
              <select
                id="mongo-collection-select"
                v-model="selectedCollection"
                :disabled="collectionsLoading || collections.length === 0"
                :class="[
                  theme.isDark.value
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900',
                  'w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50'
                ]"
              >
                <option value="">
                  {{
                    collectionsLoading
                      ? 'Loading...'
                      : selectedDatabase
                        ? 'Select a collection'
                        : 'Select database first'
                  }}
                </option>
                <option
                  v-for="col in collections"
                  :key="col.name"
                  :value="col.name"
                >
                  {{ col.name }}
                </option>
              </select>
            </div>

            <!-- Refresh Cache Button -->
            <div class="flex items-end">
              <div class="relative group">
                <button
                  :disabled="cacheClearing || !selectedClusterName || databases.length === 0"
                  :class="[
                    theme.isDark.value
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-amber-500 hover:bg-amber-600 text-white',
                    'p-2.5 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed'
                  ]"
                  @click="showClearCacheConfirm = true"
                >
                  <svg
                    :class="[cacheClearing ? 'animate-spin' : '', 'w-5 h-5']"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
                <!-- Tooltip -->
                <div
                  :class="[
                    theme.isDark.value ? 'bg-gray-700 text-gray-200' : 'bg-gray-900 text-white',
                    'absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10'
                  ]"
                >
                  Refresh dropdown caches for the selected cluster
                  <div
                    :class="[
                      theme.isDark.value ? 'border-t-gray-700' : 'border-t-gray-900',
                      'absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent'
                    ]"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Error Messages -->
          <p
            v-if="clustersError"
            class="mt-4 text-sm text-red-600 dark:text-red-400"
          >
            {{ clustersError }}
          </p>
          <p
            v-if="databasesError"
            class="mt-4 text-sm text-red-600 dark:text-red-400"
          >
            {{ databasesError }}
          </p>
          <p
            v-if="collectionsError"
            class="mt-4 text-sm text-red-600 dark:text-red-400"
          >
            {{ collectionsError }}
          </p>

          <!-- Cache Clear Success -->
          <div
            v-if="cacheClearResult"
            :class="[
              theme.isDark.value
                ? 'bg-green-900/30 border-green-700'
                : 'bg-green-50 border-green-200',
              'mt-4 border rounded-lg p-3 flex items-center justify-between'
            ]"
          >
            <div class="flex items-center gap-2">
              <svg
                :class="[theme.isDark.value ? 'text-green-400' : 'text-green-600', 'w-5 h-5']"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span
                :class="[
                  theme.isDark.value ? 'text-green-300' : 'text-green-800',
                  'text-sm font-medium'
                ]"
              >
                Cache cleared successfully. Cleared {{ cacheClearResult.total }} entries.
              </span>
            </div>
            <button
              :class="[
                theme.isDark.value
                  ? 'text-green-400 hover:text-green-200'
                  : 'text-green-600 hover:text-green-800'
              ]"
              @click="cacheClearResult = null"
            >
              <svg
                class="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Clear Cache Confirmation Modal -->
      <div
        v-if="showClearCacheConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="showClearCacheConfirm = false"
      >
        <div
          :class="[
            theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
            'border rounded-lg shadow-xl max-w-md w-full mx-4 p-6'
          ]"
        >
          <div class="flex items-start gap-4">
            <div
              :class="[
                theme.isDark.value
                  ? 'bg-amber-900/50 text-amber-300'
                  : 'bg-amber-100 text-amber-600',
                'p-2 rounded-lg'
              ]"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div class="flex-1">
              <h3
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-lg font-semibold'
                ]"
              >
                Refresh Dropdown Caches
              </h3>
              <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-2 text-sm']">
                This will clear all cached data for
                <span class="font-medium">{{ selectedClusterName }}</span> including databases and
                collections.
              </p>
              <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'mt-2 text-sm']">
                The dropdowns will be refreshed with fresh data from MongoDB Atlas.
              </p>
            </div>
          </div>
          <div class="mt-6 flex justify-end gap-3">
            <button
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700',
                'px-4 py-2 rounded-md text-sm font-medium transition-colors'
              ]"
              @click="showClearCacheConfirm = false"
            >
              Cancel
            </button>
            <button
              :class="[
                theme.isDark.value
                  ? 'bg-amber-600 hover:bg-amber-700 text-white'
                  : 'bg-amber-500 hover:bg-amber-600 text-white',
                'px-4 py-2 rounded-md text-sm font-medium transition-colors'
              ]"
              @click="clearCache"
            >
              Yes, Refresh Cache
            </button>
          </div>
        </div>
      </div>

      <!-- Index Usage Section -->
      <div
        v-if="selectedCollection"
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg overflow-hidden'
        ]"
      >
        <div
          :class="[
            theme.isDark.value ? 'border-gray-700' : 'border-gray-200',
            'px-6 py-4 border-b flex flex-col md:flex-row md:items-center md:justify-between gap-4'
          ]"
        >
          <div>
            <h2
              :class="[
                theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                'text-lg font-semibold'
              ]"
            >
              Index Usage
            </h2>
            <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm mt-1']">
              {{ selectedClusterName }} / {{ selectedDatabase }} / {{ selectedCollection }}
            </p>
          </div>

          <!-- Filters & Sort -->
          <div class="flex flex-wrap items-center gap-3">
            <!-- Filter by TTL -->
            <label
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center gap-2 text-sm cursor-pointer'
              ]"
            >
              <input
                v-model="showOnlyTTL"
                type="checkbox"
                class="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              TTL
            </label>

            <!-- Filter by Unique -->
            <label
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center gap-2 text-sm cursor-pointer'
              ]"
            >
              <input
                v-model="showOnlyUnique"
                type="checkbox"
                class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Unique
            </label>

            <!-- Filter by Compound -->
            <label
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center gap-2 text-sm cursor-pointer'
              ]"
            >
              <input
                v-model="showOnlyCompound"
                type="checkbox"
                class="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
              />
              Compound
            </label>

            <!-- Filter zero ops -->
            <label
              :class="[
                theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                'flex items-center gap-2 text-sm cursor-pointer'
              ]"
            >
              <input
                v-model="showOnlyZeroOps"
                type="checkbox"
                class="rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              Zero ops
            </label>

            <!-- Sort -->
            <select
              v-model="sortBy"
              :class="[
                theme.isDark.value
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900',
                'rounded-md border px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
              ]"
            >
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="ops-asc">Ops (Low to High)</option>
              <option value="ops-desc">Ops (High to Low)</option>
              <option value="size-asc">Size (Small to Large)</option>
              <option value="size-desc">Size (Large to Small)</option>
            </select>

            <!-- Refresh Button -->
            <button
              :disabled="indexUsageLoading"
              :class="[
                theme.isDark.value
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white',
                'px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
              ]"
              @click="loadIndexUsage"
            >
              {{ indexUsageLoading ? 'Loading...' : 'Refresh' }}
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div
          v-if="indexUsageLoading"
          class="p-12 flex items-center justify-center"
        >
          <div class="flex items-center gap-3">
            <svg
              class="animate-spin h-5 w-5 text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              />
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span :class="theme.isDark.value ? 'text-gray-300' : 'text-gray-600'">
              Loading index usage data...
            </span>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="indexUsageError"
          class="p-8 text-center"
        >
          <p class="text-red-600 dark:text-red-400">{{ indexUsageError }}</p>
          <button
            class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
            @click="loadIndexUsage"
          >
            Retry
          </button>
        </div>

        <!-- Index Usage Table -->
        <div
          v-else-if="filteredIndexes.length > 0"
          class="overflow-x-auto"
        >
          <!-- Collection Stats -->
          <div
            v-if="collectionStats"
            :class="[
              theme.isDark.value
                ? 'bg-gradient-to-r from-blue-900/30 to-indigo-900/30 border-blue-800'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200',
              'px-6 py-4 border-b'
            ]"
          >
            <div class="flex items-center gap-2 mb-3">
              <svg
                :class="[theme.isDark.value ? 'text-blue-400' : 'text-blue-600', 'w-5 h-5']"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"
                />
              </svg>
              <span
                :class="[
                  theme.isDark.value ? 'text-blue-300' : 'text-blue-700',
                  'text-sm font-medium'
                ]"
              >
                Collection Statistics
              </span>
            </div>
            <div class="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div
                :class="[
                  theme.isDark.value ? 'bg-gray-800/50' : 'bg-white/80',
                  'rounded-lg px-4 py-3'
                ]"
              >
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'text-xs uppercase tracking-wider'
                  ]"
                >
                  Documents
                </div>
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'text-lg font-semibold mt-1'
                  ]"
                >
                  {{ collectionStats.count.toLocaleString() }}
                </div>
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'bg-gray-800/50' : 'bg-white/80',
                  'rounded-lg px-4 py-3'
                ]"
              >
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'text-xs uppercase tracking-wider'
                  ]"
                >
                  Data Size
                </div>
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'text-lg font-semibold mt-1'
                  ]"
                >
                  {{ formatBytes(collectionStats.sizeBytes) }}
                </div>
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'bg-gray-800/50' : 'bg-white/80',
                  'rounded-lg px-4 py-3'
                ]"
              >
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'text-xs uppercase tracking-wider'
                  ]"
                >
                  Avg Doc Size
                </div>
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'text-lg font-semibold mt-1'
                  ]"
                >
                  {{ formatBytes(collectionStats.avgObjSize) }}
                </div>
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'bg-gray-800/50' : 'bg-white/80',
                  'rounded-lg px-4 py-3'
                ]"
              >
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                    'text-xs uppercase tracking-wider'
                  ]"
                >
                  Storage Size
                </div>
                <div
                  :class="[
                    theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                    'text-lg font-semibold mt-1'
                  ]"
                >
                  {{ formatBytes(collectionStats.storageSizeBytes) }}
                </div>
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'bg-gray-800/50' : 'bg-white/80',
                  'rounded-lg px-4 py-3'
                ]"
              >
                <div
                  :class="[
                    theme.isDark.value ? 'text-indigo-400' : 'text-indigo-600',
                    'text-xs uppercase tracking-wider'
                  ]"
                >
                  Total Index Size
                </div>
                <div
                  :class="[
                    theme.isDark.value ? 'text-indigo-300' : 'text-indigo-700',
                    'text-lg font-semibold mt-1'
                  ]"
                >
                  {{ formatBytes(collectionStats.totalIndexSizeBytes) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Summary Stats -->
          <div
            :class="[
              theme.isDark.value ? 'bg-gray-700/30 border-gray-700' : 'bg-gray-50 border-gray-200',
              'px-6 py-3 border-b grid grid-cols-3 md:grid-cols-6 gap-4'
            ]"
          >
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Total
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ indexes.length }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Zero Ops
              </div>
              <div
                :class="[
                  zeroOpsCount > 0
                    ? 'text-amber-500'
                    : theme.isDark.value
                      ? 'text-gray-100'
                      : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ zeroOpsCount }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-purple-400' : 'text-purple-600',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                TTL
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-purple-300' : 'text-purple-700',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ ttlIndexCount }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-blue-400' : 'text-blue-600',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Unique
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-blue-300' : 'text-blue-700',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ uniqueIndexCount }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-cyan-400' : 'text-cyan-600',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Compound
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-cyan-300' : 'text-cyan-700',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ compoundIndexCount }}
              </div>
            </div>
            <div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                  'text-xs uppercase tracking-wider'
                ]"
              >
                Showing
              </div>
              <div
                :class="[
                  theme.isDark.value ? 'text-gray-100' : 'text-gray-900',
                  'text-xl font-semibold mt-1'
                ]"
              >
                {{ filteredIndexes.length }}
              </div>
            </div>
          </div>

          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead
              :class="[
                theme.isDark.value ? 'bg-gray-700/50' : 'bg-gray-50',
                'text-left text-xs font-medium uppercase tracking-wider'
              ]"
            >
              <tr>
                <th
                  :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3 w-8']"
                />
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Index Name
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Key Definition
                </th>
                <th
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-3 text-right'
                  ]"
                >
                  Total Ops
                </th>
                <th :class="[theme.isDark.value ? 'text-gray-300' : 'text-gray-600', 'px-6 py-3']">
                  Since
                </th>
                <th
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-3 text-center'
                  ]"
                >
                  Type
                </th>
                <th
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-3 text-right'
                  ]"
                >
                  Size
                </th>
                <th
                  :class="[
                    theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                    'px-6 py-3 text-center'
                  ]"
                >
                  Status
                </th>
              </tr>
            </thead>
            <tbody
              :class="[theme.isDark.value ? 'divide-gray-700' : 'divide-gray-200', 'divide-y']"
            >
              <template
                v-for="(idx, i) in filteredIndexes"
                :key="idx.name"
              >
                <!-- Main Row -->
                <tr
                  :class="[
                    theme.isDark.value
                      ? 'bg-gray-800 hover:bg-gray-700/50'
                      : 'bg-white hover:bg-gray-50',
                    'cursor-pointer transition-colors'
                  ]"
                  @click="toggleRow(i)"
                >
                  <td class="px-6 py-4">
                    <svg
                      :class="[
                        theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                        'w-4 h-4 transition-transform',
                        expandedRows.includes(i) ? 'rotate-90' : ''
                      ]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </td>
                  <td
                    :class="[
                      theme.isDark.value ? 'text-gray-200' : 'text-gray-900',
                      'px-6 py-4 text-sm font-medium'
                    ]"
                  >
                    {{ idx.name }}
                  </td>
                  <td
                    :class="[
                      theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                      'px-6 py-4 text-sm font-mono text-xs'
                    ]"
                  >
                    {{ formatKey(idx.key) }}
                  </td>
                  <td
                    :class="[
                      idx.usage.ops === 0
                        ? 'text-amber-500'
                        : theme.isDark.value
                          ? 'text-gray-100'
                          : 'text-gray-900',
                      'px-6 py-4 text-sm font-semibold text-right'
                    ]"
                  >
                    {{ idx.usage.ops.toLocaleString() }}
                  </td>
                  <td
                    :class="[
                      theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                      'px-6 py-4 text-sm'
                    ]"
                  >
                    {{ formatDate(idx.usage.since) }}
                  </td>
                  <td class="px-6 py-4">
                    <div class="flex flex-wrap justify-center gap-1">
                      <!-- TTL Badge -->
                      <span
                        v-if="idx.ttlSeconds"
                        :class="[
                          theme.isDark.value
                            ? 'bg-purple-900/50 text-purple-300'
                            : 'bg-purple-100 text-purple-800',
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                        ]"
                      >
                        TTL {{ formatTTL(idx.ttlSeconds) }}
                      </span>
                      <!-- Unique Badge -->
                      <span
                        v-if="isUniqueIndex(idx)"
                        :class="[
                          theme.isDark.value
                            ? 'bg-blue-900/50 text-blue-300'
                            : 'bg-blue-100 text-blue-800',
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                        ]"
                      >
                        Unique
                      </span>
                      <!-- Compound Badge -->
                      <span
                        v-if="Object.keys(idx.key).length > 1"
                        :class="[
                          theme.isDark.value
                            ? 'bg-cyan-900/50 text-cyan-300'
                            : 'bg-cyan-100 text-cyan-800',
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                        ]"
                      >
                        Compound ({{ Object.keys(idx.key).length }})
                      </span>
                      <!-- Standard Badge (only if no other type) -->
                      <span
                        v-if="
                          !idx.ttlSeconds &&
                          !isUniqueIndex(idx) &&
                          Object.keys(idx.key).length === 1
                        "
                        :class="[
                          theme.isDark.value
                            ? 'bg-gray-700 text-gray-300'
                            : 'bg-gray-100 text-gray-700',
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                        ]"
                      >
                        Standard
                      </span>
                    </div>
                  </td>
                  <td
                    :class="[
                      theme.isDark.value ? 'text-gray-300' : 'text-gray-600',
                      'px-6 py-4 text-right text-sm'
                    ]"
                  >
                    {{ idx.sizeBytes ? formatBytes(idx.sizeBytes) : '-' }}
                  </td>
                  <td class="px-6 py-4 text-center">
                    <span
                      v-if="isUnusedIndex(idx)"
                      :class="[
                        theme.isDark.value
                          ? 'bg-amber-900/50 text-amber-300'
                          : 'bg-amber-100 text-amber-800',
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                      ]"
                    >
                      Unused
                    </span>
                    <span
                      v-else
                      :class="[
                        theme.isDark.value
                          ? 'bg-green-900/50 text-green-300'
                          : 'bg-green-100 text-green-800',
                        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium'
                      ]"
                    >
                      Active
                    </span>
                  </td>
                </tr>

                <!-- Expanded Row - Per Host Details -->
                <tr
                  v-if="expandedRows.includes(i)"
                  :class="[theme.isDark.value ? 'bg-gray-900' : 'bg-gray-50']"
                >
                  <td
                    colspan="8"
                    class="px-6 py-4"
                  >
                    <div class="ml-10">
                      <h4
                        :class="[
                          theme.isDark.value ? 'text-gray-300' : 'text-gray-700',
                          'text-sm font-medium mb-3'
                        ]"
                      >
                        Per-Host Usage Breakdown
                      </h4>
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div
                          v-for="host in idx.usage.perHost"
                          :key="host.host"
                          :class="[
                            theme.isDark.value
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-white border-gray-200',
                            'border rounded-lg p-4'
                          ]"
                        >
                          <div class="flex items-center justify-between mb-2">
                            <span
                              :class="[
                                theme.isDark.value ? 'text-gray-400' : 'text-gray-500',
                                'text-xs font-mono truncate max-w-[200px]'
                              ]"
                              :title="host.host"
                            >
                              {{ truncateHost(host.host) }}
                            </span>
                            <span
                              :class="[
                                host.ops === 0
                                  ? 'text-amber-500'
                                  : theme.isDark.value
                                    ? 'text-gray-100'
                                    : 'text-gray-900',
                                'text-sm font-semibold'
                              ]"
                            >
                              {{ host.ops.toLocaleString() }} ops
                            </span>
                          </div>
                          <div
                            :class="[
                              theme.isDark.value ? 'text-gray-500' : 'text-gray-400',
                              'text-xs'
                            ]"
                          >
                            Since {{ formatDate(host.since) }}
                          </div>
                          <!-- Visual bar -->
                          <div
                            class="mt-2 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden"
                          >
                            <div
                              :class="[host.ops === 0 ? 'bg-amber-500' : 'bg-green-500']"
                              :style="{ width: getOpsBarWidth(host.ops, idx.usage.perHost) }"
                              class="h-full transition-all duration-300"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!indexUsageLoading && !indexUsageError"
          class="p-12 text-center"
        >
          <svg
            :class="[
              theme.isDark.value ? 'text-gray-600' : 'text-gray-400',
              'w-12 h-12 mx-auto mb-4'
            ]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm']">
            {{
              showOnlyTTL || showOnlyUnique || showOnlyCompound || showOnlyZeroOps
                ? 'No indexes match the current filters'
                : 'No indexes found for this collection'
            }}
          </p>
        </div>
      </div>

      <!-- Prompt to select collection -->
      <div
        v-else
        :class="[
          theme.isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-12 text-center'
        ]"
      >
        <svg
          :class="[
            theme.isDark.value ? 'text-gray-600' : 'text-gray-400',
            'w-16 h-16 mx-auto mb-4'
          ]"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <h3
          :class="[
            theme.isDark.value ? 'text-gray-200' : 'text-gray-900',
            'text-lg font-medium mb-2'
          ]"
        >
          Select a Collection
        </h3>
        <p :class="[theme.isDark.value ? 'text-gray-400' : 'text-gray-600', 'text-sm']">
          Choose an environment, cluster, database, and collection above to view index usage
          statistics.
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject, ref, watch, onMounted, computed } from 'vue'
import { useTheme } from '../../utils/useTheme'
import { mongoService, isUniqueIndex } from '../../services/database/mongoService'
import type {
  MongoCluster,
  MongoCollectionItem,
  MongoEnv,
  MongoIndex,
  CacheClearResponse
} from '../../services/database/mongoService'

export default defineComponent({
  name: 'DatabaseMongo',
  setup() {
    const theme = inject('theme', useTheme())

    // Selection state
    const mongoEnv = ref<MongoEnv>('staging')
    const selectedClusterName = ref('')
    const selectedDatabase = ref('')
    const selectedCollection = ref('')

    // Clusters
    const clusterList = ref<MongoCluster[]>([])
    const clustersLoading = ref(false)
    const clustersError = ref('')

    // Databases
    const databases = ref<string[]>([])
    const databasesLoading = ref(false)
    const databasesError = ref('')

    // Collections
    const collections = ref<MongoCollectionItem[]>([])
    const collectionsLoading = ref(false)
    const collectionsError = ref('')

    // Index Usage
    const indexes = ref<MongoIndex[]>([])
    const indexUsageLoading = ref(false)
    const indexUsageError = ref('')
    const collectionStats = ref<{
      sizeBytes: number
      count: number
      avgObjSize: number
      storageSizeBytes: number
      totalIndexSizeBytes: number
    } | null>(null)

    // UI State
    const expandedRows = ref<number[]>([])
    const showOnlyTTL = ref(false)
    const showOnlyUnique = ref(false)
    const showOnlyCompound = ref(false)
    const showOnlyZeroOps = ref(false)
    const sortBy = ref('name-asc')

    // Cache Management State
    const cacheClearing = ref(false)
    const cacheClearResult = ref<CacheClearResponse | null>(null)
    const showClearCacheConfirm = ref(false)

    // Computed: Filtered and sorted indexes
    const filteredIndexes = computed(() => {
      let result = [...indexes.value]

      // Filter by TTL
      if (showOnlyTTL.value) {
        result = result.filter((idx) => idx.ttlSeconds !== undefined)
      }

      // Filter by Unique
      if (showOnlyUnique.value) {
        result = result.filter((idx) => isUniqueIndex(idx))
      }

      // Filter by Compound
      if (showOnlyCompound.value) {
        result = result.filter((idx) => Object.keys(idx.key).length > 1)
      }

      // Filter by zero ops
      if (showOnlyZeroOps.value) {
        result = result.filter((idx) => isUnusedIndex(idx))
      }

      // Sort
      const [field, direction] = sortBy.value.split('-')
      result.sort((a, b) => {
        let comparison = 0
        if (field === 'name') {
          comparison = a.name.localeCompare(b.name)
        } else if (field === 'ops') {
          comparison = a.usage.ops - b.usage.ops
        } else if (field === 'size') {
          comparison = (a.sizeBytes || 0) - (b.sizeBytes || 0)
        }
        return direction === 'desc' ? -comparison : comparison
      })

      return result
    })

    // Computed: Statistics
    const zeroOpsCount = computed(() => indexes.value.filter((idx) => isUnusedIndex(idx)).length)

    const ttlIndexCount = computed(
      () => indexes.value.filter((idx) => idx.ttlSeconds !== undefined).length
    )

    const uniqueIndexCount = computed(
      () => indexes.value.filter((idx) => isUniqueIndex(idx)).length
    )

    const compoundIndexCount = computed(
      () => indexes.value.filter((idx) => Object.keys(idx.key).length > 1).length
    )

    // Load clusters
    async function loadClusters() {
      clustersLoading.value = true
      clustersError.value = ''
      try {
        const res = await mongoService.getClusters(mongoEnv.value)
        clusterList.value = res.clusters ?? []
      } catch (e) {
        clustersError.value = e instanceof Error ? e.message : 'Unable to load clusters'
      } finally {
        clustersLoading.value = false
      }
    }

    // Load databases
    async function loadDatabases() {
      if (!selectedClusterName.value) {
        databases.value = []
        return
      }
      databasesLoading.value = true
      databasesError.value = ''
      try {
        const res = await mongoService.getDatabases(selectedClusterName.value, mongoEnv.value)
        databases.value = res.databases ?? []
      } catch (e) {
        databasesError.value = e instanceof Error ? e.message : 'Unable to load databases'
        databases.value = []
      } finally {
        databasesLoading.value = false
      }
    }

    // Load collections
    async function loadCollections() {
      if (!selectedClusterName.value || !selectedDatabase.value) {
        collections.value = []
        return
      }
      collectionsLoading.value = true
      collectionsError.value = ''
      try {
        const res = await mongoService.getCollections(
          selectedClusterName.value,
          selectedDatabase.value,
          mongoEnv.value
        )
        collections.value = res.collections ?? []
      } catch (e) {
        collectionsError.value = e instanceof Error ? e.message : 'Unable to load collections'
        collections.value = []
      } finally {
        collectionsLoading.value = false
      }
    }

    // Load index usage
    async function loadIndexUsage() {
      if (!selectedClusterName.value || !selectedDatabase.value || !selectedCollection.value) {
        indexes.value = []
        collectionStats.value = null
        return
      }
      indexUsageLoading.value = true
      indexUsageError.value = ''
      expandedRows.value = []
      try {
        const res = await mongoService.getIndexUsage(
          selectedClusterName.value,
          selectedDatabase.value,
          selectedCollection.value,
          mongoEnv.value
        )
        indexes.value = res.indexes ?? []
        collectionStats.value = res.collectionStats ?? null
      } catch (e) {
        indexUsageError.value = e instanceof Error ? e.message : 'Unable to load index usage'
        indexes.value = []
        collectionStats.value = null
      } finally {
        indexUsageLoading.value = false
      }
    }

    // Clear cache for current cluster
    async function clearCache() {
      if (!selectedClusterName.value) return

      showClearCacheConfirm.value = false
      cacheClearing.value = true
      cacheClearResult.value = null

      try {
        const result = await mongoService.clearClusterCache(
          selectedClusterName.value,
          mongoEnv.value
        )
        cacheClearResult.value = result

        // Reset selections to initial state for fresh start
        selectedClusterName.value = ''
        selectedDatabase.value = ''
        selectedCollection.value = ''
        databases.value = []
        collections.value = []
        indexes.value = []
        collectionStats.value = null

        // Reload clusters with fresh data
        await loadClusters()
      } catch {
        // Cache clear failed; user can retry
      } finally {
        cacheClearing.value = false
      }
    }

    // Event handlers
    function onEnvChange() {
      selectedClusterName.value = ''
      selectedDatabase.value = ''
      selectedCollection.value = ''
      databases.value = []
      collections.value = []
      indexes.value = []
      collectionStats.value = null
      loadClusters()
    }

    function toggleRow(index: number) {
      const idx = expandedRows.value.indexOf(index)
      if (idx === -1) {
        expandedRows.value.push(index)
      } else {
        expandedRows.value.splice(idx, 1)
      }
    }

    // Utility functions
    function formatKey(key: Record<string, number | string>) {
      return Object.entries(key)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ')
    }

    function formatDate(dateStr: string) {
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric'
        })
      } catch {
        return dateStr
      }
    }

    function formatTTL(seconds: number) {
      if (seconds >= 86400) {
        return `${Math.round(seconds / 86400)}d`
      } else if (seconds >= 3600) {
        return `${Math.round(seconds / 3600)}h`
      } else if (seconds >= 60) {
        return `${Math.round(seconds / 60)}m`
      }
      return `${seconds}s`
    }

    function formatBytes(bytes: number): string {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      const value = bytes / Math.pow(k, i)
      return `${value.toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`
    }

    function truncateHost(host: string) {
      const parts = host.split('.')
      if (parts.length > 1) {
        return parts[0]
      }
      return host
    }

    function isUnusedIndex(idx: MongoIndex) {
      // An index is unused if all hosts have 0 ops
      return idx.usage.perHost.every((h) => h.ops === 0)
    }

    function getOpsBarWidth(ops: number, allHosts: { ops: number }[]) {
      const maxOps = Math.max(...allHosts.map((h) => h.ops), 1)
      const percentage = (ops / maxOps) * 100
      return `${Math.max(percentage, ops > 0 ? 5 : 0)}%`
    }

    // Watchers
    watch(selectedClusterName, () => {
      selectedDatabase.value = ''
      selectedCollection.value = ''
      collections.value = []
      indexes.value = []
      collectionStats.value = null
      loadDatabases()
    })

    watch(selectedDatabase, () => {
      selectedCollection.value = ''
      indexes.value = []
      collectionStats.value = null
      loadCollections()
    })

    watch(selectedCollection, () => {
      indexes.value = []
      collectionStats.value = null
      if (selectedCollection.value) {
        loadIndexUsage()
      }
    })

    // Initial load
    onMounted(() => {
      loadClusters()
    })

    return {
      theme,
      mongoEnv,
      onEnvChange,
      selectedClusterName,
      selectedDatabase,
      selectedCollection,
      clusterList,
      clustersLoading,
      clustersError,
      databases,
      databasesLoading,
      databasesError,
      collections,
      collectionsLoading,
      collectionsError,
      indexes,
      indexUsageLoading,
      indexUsageError,
      collectionStats,
      loadIndexUsage,
      filteredIndexes,
      zeroOpsCount,
      ttlIndexCount,
      uniqueIndexCount,
      compoundIndexCount,
      expandedRows,
      showOnlyTTL,
      showOnlyUnique,
      showOnlyCompound,
      showOnlyZeroOps,
      sortBy,
      toggleRow,
      formatKey,
      formatDate,
      formatTTL,
      formatBytes,
      truncateHost,
      isUnusedIndex,
      isUniqueIndex,
      getOpsBarWidth,
      // Cache management
      cacheClearing,
      cacheClearResult,
      showClearCacheConfirm,
      clearCache
    }
  }
})
</script>
